<script setup>
import { ref, computed, watch, onMounted, defineProps, defineEmits } from 'vue'
import { useProviderForm } from '@/composables/useProviderForm.js'
import { useProviderThumbnail } from '@/composables/useProviderThumbnail.js'
import { COUNTRY_OPTIONS, PROVIDER_TYPE_OPTIONS, FORM_MESSAGES, FILE_VALIDATION } from '@/constants/providerOptions.js'
import CategoryMultiSelect from '@/components/Anonymization/CategoryMultiSelect.vue'
import ProviderAPI from '@/api/Provider'

const emit = defineEmits(['slideover-close', 'success'])
const props = defineProps({
  mode: {
    type: String,
    default: 'create', // 'create' or 'edit'
    validator: value => ['create', 'edit'].includes(value)
  },
  providerData: {
    type: Object,
    default: () => ({})
  },
  initialCategories: {
    type: Array,
    default: () => []
  }
})

// Use composables
const {
  formData,
  isSubmitting,
  status,
  statusMessage,
  isValid,
  modalTitle,
  resetForm,
  fetchProviderData,
  submitForm
} = useProviderForm()

const {
  thumbnailFile,
  thumbnailPreview,
  isUploadingThumbnail,
  handleThumbnailChange,
  removeThumbnail,
  setExistingThumbnail,
  resetThumbnail
} = useProviderThumbnail()

// Category state
const availableCategories = ref([])
const loadingCategories = ref(false)

// Load categories from API
const loadCategories = async () => {
  loadingCategories.value = true
  try {
    availableCategories.value = await ProviderAPI.getCategories()
  } catch (error) {
    console.error('Failed to load categories:', error)
    availableCategories.value = []
  } finally {
    loadingCategories.value = false
  }
}

// Computed properties
const currentModalTitle = computed(() => {
  if (props.mode === 'create' && props.providerData && props.providerData.name) {
    return `Create Profile: ${props.providerData.name}`
  }
  return modalTitle.value(props.mode)
})
const currentSubmitButtonText = computed(() => {
  if (isSubmitting.value) {
    return props.mode === 'create' ? 'Creating...' : 'Updating...'
  }
  return props.mode === 'create' ? 'Create Provider' : 'Update Provider'
})

// Custom reset that includes thumbnail reset
const resetFormAndThumbnail = () => {
  resetForm()
  resetThumbnail()
}

// Enhanced fetch that sets thumbnail preview
const fetchAndSetProviderData = async (providerName) => {
  try {
    const result = await fetchProviderData(providerName)
    if (result && result.thumbnail_url) {
      setExistingThumbnail(result.thumbnail_url)
    }
    return result
  } catch (error) {
    console.error('Error fetching provider data:', error)
    throw error
  }
}

// Watch for prop changes to populate form
watch(() => props.providerData, (newData) => {
  if (props.mode === 'edit' && newData && newData.name) {
    // For edit mode, fetch fresh data from API
    fetchAndSetProviderData(newData.name)
  } else if (props.mode === 'create') {
    // For create mode, reset to empty form
    resetFormAndThumbnail()
    // Pre-fill name if provided (for creating from missing providers)
    if (newData && newData.name) {
      formData.value.name = newData.name
    }
  }
}, { immediate: true })

// Custom submit handler that includes auto-close and custom event dispatch
const handleSubmit = async () => {
  try {
    const result = await submitForm(
      props.mode,
      props.providerData,
      thumbnailFile.value,
      (data) => emit('success', data)
    )

    if (result && result.status === 'success') {
      // Auto-close after success and refresh any parent components
      setTimeout(() => {
        // Trigger a custom event that the main store can listen to
        window.dispatchEvent(new CustomEvent('provider-updated', { detail: result.data || formData.value }))
        emit('slideover-close')
      }, 2000)
    }
  } catch (error) {
    console.error('Submit error:', error)
  }
}

// Enhanced thumbnail change handler with error reporting
const handleThumbnailChangeWithErrors = (event) => {
  handleThumbnailChange(event, (errorMessage) => {
    if (errorMessage) {
      status.value = 'error'
      statusMessage.value = errorMessage
    } else {
      status.value = null
      statusMessage.value = ''
    }
  })
}

// Handle thumbnail removal with API call for existing providers
const handleRemoveThumbnail = async () => {
  const providerName = props.mode === 'edit' ? props.providerData?.name : null

  await removeThumbnail(
    providerName,
    () => {
      // Success callback
      status.value = 'success'
      statusMessage.value = 'Thumbnail removed successfully'

      // Dispatch update event
      window.dispatchEvent(new CustomEvent('provider-updated', {
        detail: { name: providerName }
      }))

      // Clear success message after 2 seconds
      setTimeout(() => {
        status.value = null
        statusMessage.value = ''
      }, 2000)
    },
    (errorMessage) => {
      // Error callback
      status.value = 'error'
      statusMessage.value = errorMessage
    }
  )
}

// Watch for initialCategories changes
watch(() => props.initialCategories, (newCategories) => {
  if (newCategories && newCategories.length > 0) {
    formData.value.categories = [...newCategories]
  }
}, { immediate: true })

onMounted(() => {
  // Initialize form based on mode
  // Only reset if in create mode AND no provider name was provided
  if (props.mode === 'create' && (!props.providerData || !props.providerData.name)) {
    resetFormAndThumbnail()
  }

  // Load available categories
  loadCategories()
})
</script>

<template>
  <div class="provider-slideover">
    <!-- Header -->
    <div class="box-header">
      <h3>{{ currentModalTitle }}</h3>
    </div>

    <!-- Content -->
    <div class="box-content">
      <!-- Success State -->
      <div v-if="status === 'success'" class="success-state">
        <div class="success-icon">
          <font-awesome-icon :icon="['fas', 'check-circle']" />
        </div>
        <h4>{{ props.mode === 'create' ? 'Provider Created!' : 'Provider Updated!' }}</h4>
        <p>{{ props.mode === 'create' ? 'The new provider has been successfully created.' : 'The provider profile has been successfully updated.' }}</p>
      </div>

      <!-- Form -->
      <form v-else class="provider-form" @submit.prevent="handleSubmit()">
        <div class="form-content">
          <!-- Error Message -->
          <div v-if="status === 'error'" class="alert alert-error mb-3">
            <font-awesome-icon :icon="['fas', 'exclamation-triangle']" />
            <span>{{ statusMessage }}</span>
          </div>

          <!-- Provider Name -->
          <div class="form-group">
            <label for="providerName">Provider Name *</label>
            <div class="input-wrapper">
              <input
                type="text"
                v-model="formData.name"
                id="providerName"
                :placeholder="'Enter provider name'"
                :disabled="props.mode === 'edit' || (props.mode === 'create' && props.providerData && props.providerData.name)"
                required
              >
            </div>
            <div v-if="props.mode === 'edit' || (props.mode === 'create' && props.providerData && props.providerData.name)" class="form-help">
              {{ props.mode === 'edit' ? FORM_MESSAGES.PROVIDER_NAME_READONLY : 'Creating profile for existing provider' }}
            </div>
          </div>

          <!-- Thumbnail Upload -->
          <div class="form-group">
            <label>Thumbnail</label>
            <div class="thumbnail-section">
              <!-- Thumbnail Preview -->
              <div class="thumbnail-preview">
                <img
                  v-if="thumbnailPreview"
                  :src="thumbnailPreview"
                  alt="Thumbnail Preview"
                  class="preview-image"
                />
                <div v-else class="preview-placeholder">
                  <font-awesome-icon :icon="['fas', 'image']" />
                  <span>No thumbnail</span>
                </div>
              </div>

              <!-- Upload Controls -->
              <div class="thumbnail-controls">
                <input
                  type="file"
                  id="thumbnailInput"
                  accept="image/jpeg,image/png,image/webp,image/svg+xml"
                  @change="handleThumbnailChangeWithErrors"
                  class="thumbnail-input"
                />
                <label for="thumbnailInput" class="upload-btn">
                  <font-awesome-icon :icon="['fas', 'upload']" />
                  Choose Image
                </label>
                <button
                  v-if="thumbnailPreview"
                  type="button"
                  @click="handleRemoveThumbnail"
                  class="remove-btn"
                  :disabled="isUploadingThumbnail"
                >
                  <font-awesome-icon v-if="isUploadingThumbnail" :icon="['fas', 'spinner']" spin />
                  <font-awesome-icon v-else :icon="['fas', 'trash']" />
                  Remove
                </button>
              </div>
            </div>
            <div class="form-help">
              Supported: {{ FILE_VALIDATION.SUPPORTED_FORMATS }}. Max size: {{ FILE_VALIDATION.MAX_SIZE_TEXT }}
            </div>
          </div>

          <!-- Display Name -->
          <div class="form-group">
            <label for="displayName">Display Name *</label>
            <div class="input-wrapper">
              <input
                type="text"
                v-model="formData.display_name"
                id="displayName"
                placeholder="Enter display name"
                required
              >
            </div>
          </div>

          <!-- Description -->
          <div class="form-group">
            <label for="description">Description</label>
            <div class="input-wrapper">
              <textarea
                v-model="formData.description"
                id="description"
                placeholder="Enter description"
                rows="3"
              ></textarea>
            </div>
          </div>

          <!-- Website URL -->
          <div class="form-group">
            <label for="websiteUrl">Website URL</label>
            <div class="input-wrapper">
              <input
                type="url"
                v-model="formData.website_url"
                id="websiteUrl"
                placeholder="https://example.com"
              >
            </div>
          </div>

          <!-- Country -->
          <div class="form-group">
            <label for="countryCode">Country</label>
            <div class="input-wrapper">
              <select v-model="formData.country_code" id="countryCode">
                <option v-for="country in COUNTRY_OPTIONS" :key="country.code" :value="country.code">
                  {{ country.name }}
                </option>
              </select>
            </div>
          </div>

          <!-- Provider Type -->
          <div class="form-group">
            <label for="providerType">Provider Type</label>
            <div class="input-wrapper">
              <select v-model="formData.provider_type" id="providerType">
                <option v-for="type in PROVIDER_TYPE_OPTIONS" :key="type.value" :value="type.value">
                  {{ type.name }}
                </option>
              </select>
            </div>
          </div>

          <!-- Categories -->
          <div class="form-group category-field">
            <label for="categories">Categories</label>
            <CategoryMultiSelect
              v-model="formData.categories"
              :categories="availableCategories"
              :disabled="loadingCategories"
            />
            <div class="form-help">
              Select one or more categories to group this provider
            </div>
          </div>
        </div>

        <!-- Submit Button -->
        <div class="form-actions">
          <button
            type="submit"
            class="submit-btn"
            :disabled="!isValid || isSubmitting"
          >
            <font-awesome-icon v-if="isSubmitting" :icon="['fas', 'spinner']" spin />
            {{ currentSubmitButtonText }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<style scoped>
.provider-slideover {
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  color: white;
  margin: 0;
  padding: 0;
}

.box-header {
  background-color: rgba(255, 255, 255, 0.03);
  padding: 1rem 2rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  flex-shrink: 0;
  width: 100%;
  margin: 0;
  border-radius: 10px 0 0 0;
}

.box-header h3 {
  margin: 0;
  color: #E5E7EB;
  font-size: 1.1rem;
  font-weight: 500;
}

.box-content {
  flex: 1;
  padding: 2rem;
  overflow-y: auto;
  min-height: 0;
  width: 100%;
  box-sizing: border-box;
}

/* Success State */
.success-state {
  text-align: center;
  padding: 3rem 1rem;
}

.success-icon {
  font-size: 3rem;
  color: #10B981;
  margin-bottom: 1rem;
}

.success-state h4 {
  color: white;
  margin-bottom: 0.5rem;
  font-size: 1.5rem;
}

.success-state p {
  color: #9CA3AF;
  margin: 0;
}

/* Form Styles */
.provider-form {
  max-width: none;
  width: 100%;
  display: flex;
  flex-direction: column;
  height: 100%;
  margin: 0;
  padding: 0;
}

.form-content {
  flex: 1;
  overflow-y: auto;
  padding-bottom: 1rem;
  width: 100%;
  box-sizing: border-box;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group.category-field {
  overflow: visible;
  position: relative;
  z-index: 10;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  color: #E5E7EB;
  font-weight: 500;
  font-size: 0.875rem;
}

.input-wrapper {
  background-color: #171C24;
  border: 1px solid #374151;
  border-radius: 10px;
  padding: 0.75rem 1rem;
  transition: border-color 0.2s;
}

.input-wrapper:focus-within {
  border-color: #7357D2;
}

.input-wrapper input,
.input-wrapper textarea,
.input-wrapper select {
  background: transparent;
  border: none;
  outline: none;
  color: white;
  width: 100%;
  resize: none;
}

.input-wrapper input:disabled {
  color: #9CA3AF;
  cursor: not-allowed;
  opacity: 1;
}

.input-wrapper input::placeholder,
.input-wrapper textarea::placeholder {
  color: #6B7280;
}

.form-help {
  margin-top: 0.25rem;
  font-size: 0.75rem;
  color: #6B7280;
}

/* Alert */
.alert {
  padding: 0.75rem 1rem;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.alert-error {
  background-color: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: #FCA5A5;
}

/* Thumbnail Section */
.thumbnail-section {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background-color: #171C24;
  border-radius: 10px;
  border: 1px solid #374151;
}

.thumbnail-preview {
  flex-shrink: 0;
}

.preview-image {
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 8px;
}

.preview-placeholder {
  width: 60px;
  height: 60px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #374151;
  border-radius: 8px;
  color: #6B7280;
  font-size: 0.75rem;
  text-align: center;
  gap: 0.25rem;
  border: 2px dashed rgba(255, 255, 255, 0.2);
}

.preview-placeholder svg {
  font-size: 1.25rem;
}

.thumbnail-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
}

.thumbnail-input {
  display: none;
}

.upload-btn, .remove-btn {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: background-color 0.2s;
  font-weight: 500;
}

.upload-btn {
  background-color: #7357D2;
  color: white;
}

.upload-btn:hover {
  background-color: #7F62E3;
}

.remove-btn {
  background-color: #DC2626;
  color: white;
}

.remove-btn:hover {
  background-color: #EF4444;
}

/* Form Actions */
.form-actions {
  margin-top: 2rem;
  padding: 1.5rem 2rem;
  border-top: 1px solid #374151;
  text-align: center;
  background-color: #191f27;
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  box-sizing: border-box;
  margin-left: -2rem;
  margin-right: -2rem;
  margin-bottom: -2rem;
}

.submit-btn {
  padding: 0.75rem 2rem;
  background-color: #7357D2;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.95rem;
}

.submit-btn:hover:not(:disabled) {
  background-color: #7F62E3;
  transform: translateY(-1px);
}

.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

/* Custom scrollbar */
.box-content::-webkit-scrollbar,
.form-content::-webkit-scrollbar {
  width: 6px;
}

.box-content::-webkit-scrollbar-track,
.form-content::-webkit-scrollbar-track {
  background: #1f2937;
}

.box-content::-webkit-scrollbar-thumb,
.form-content::-webkit-scrollbar-thumb {
  background: #374151;
  border-radius: 3px;
}

.box-content::-webkit-scrollbar-thumb:hover,
.form-content::-webkit-scrollbar-thumb:hover {
  background: #4b5563;
}
</style>